import type { ServiceResponse } from "../core/service";
import type {
  GetMaxPriorityFeePerGasEstimateRequest,
  GetMaxPriorityFeePerGasEstimateResponse,
  InstructionActivateRequest,
  InstructionActivateResponse,
  InstructionBuildRequest,
  InstructionBuildResponse,
  InstructionDeactivateRequest,
  InstructionDeactivateResponse,
  InstructionDetailsRequest,
  InstructionDetailsResponse,
  InstructionGetAllRequest,
  InstructionGetAllResponse,
  InstructionGetSummaryRequest,
} from "../schemas/instruction";
import type { InstructionSummariesByDate } from "@schemas/instruction";

import { z } from "zod";

import {
  instructionsSchema,
  instructionSummariesByDateSchema,
} from "@schemas/instruction";

import { BaseService } from "../core/service";
import {
  getMaxPriorityFeePerGasEstimateResponseSchema,
  instructionActivateResponseSchema,
  instructionBuildResponseSchema,
  instructionDeactivateResponseSchema,
  instructionDetailsResponseSchema,
} from "../schemas/instruction";

export class InstructionService extends BaseService {
  public async getAll(
    data: InstructionGetAllRequest,
  ): Promise<ServiceResponse<InstructionGetAllResponse>> {
    return this.get("/instructions/all", {
      data,
      responseSchema: instructionsSchema,
    });
  }

  public async getSummary(
    data: InstructionGetSummaryRequest,
  ): Promise<ServiceResponse<InstructionSummariesByDate>> {
    const response = await this.get("/instructions/summary", {
      data,
      responseSchema: z.array(instructionSummariesByDateSchema),
    });

    // transform array response into record format.
    const summaryRecords = response.data.reduce<InstructionSummariesByDate>(
      (acc, curr) => Object.assign(acc, curr),
      {},
    );

    return { data: summaryRecords };
  }

  public async build(
    body: InstructionBuildRequest,
  ): Promise<ServiceResponse<InstructionBuildResponse>> {
    return this.post("/instruction/build", {
      data: body,
      responseSchema: instructionBuildResponseSchema,
    });
  }

  public async activate(
    body: InstructionActivateRequest,
  ): Promise<ServiceResponse<InstructionActivateResponse>> {
    return this.post("/instruction/new", {
      data: body,
      responseSchema: instructionActivateResponseSchema,
    });
  }

  public async deactivate(
    body: InstructionDeactivateRequest,
  ): Promise<ServiceResponse<InstructionDeactivateResponse>> {
    return this.post("/instruction/deactivate", {
      data: body,
      responseSchema: instructionDeactivateResponseSchema,
    });
  }

  public async getDetails(
    data: InstructionDetailsRequest,
  ): Promise<ServiceResponse<InstructionDetailsResponse>> {
    return this.get("/instruction/details", {
      data,
      responseSchema: instructionDetailsResponseSchema,
    });
  }

  public async getMaxPriorityFeePerGasEstimate(
    params: GetMaxPriorityFeePerGasEstimateRequest,
  ): Promise<ServiceResponse<GetMaxPriorityFeePerGasEstimateResponse>> {
    return this.get(
      `/instruction/estimate/max_priority_fee_per_gas/${params.chainId}`,
      { responseSchema: getMaxPriorityFeePerGasEstimateResponseSchema },
    );
  }
}
